import type { TokenResponseExtended as TokenResponse } from 'owa-service/lib/tokenResponseExtended';
import type { EnterpriseTokenRequestParams } from 'owa-tokenprovider-utils';
import { getUserTokenIfAvailable } from './getUserTokenIfAvailable';
import sleep from 'owa-sleep';
import { getOrigin } from 'owa-url/lib/getOrigin';
import type { LogStartPerformanceUsage } from 'owa-analytics-start';
import { fetchTokenFromServer } from './fetchTokenFromServer';
import { getResourceToken } from './getResourceToken';
import { TokenResponseType } from 'owa-tokenprovider-utils/lib/TokenResponseType';
import { isBootFeatureEnabled } from 'owa-metatags';
import { getApplicationSettings } from 'owa-application-settings';

export async function fetchTokenFromHostAppForEnterpriseWithRetry(
    perfLog: LogStartPerformanceUsage,
    retryCount: number,
    retryDelay: number,
    enterpriseParams: EnterpriseTokenRequestParams
): Promise<TokenResponse> {
    if (retryCount < 1) {
        const error = new Error('Max retrying limit (1) reached');
        perfLog.addCustomProperty('FetchFromHostAppEnterprise_Message', error?.message ?? '');
        perfLog.addCustomProperty('FetchFromHostAppEnterprise_Stack', error?.stack ?? '');
        return Promise.reject(error);
    }

    return fetchTokenFromHostAppForEnterprise(perfLog, enterpriseParams).catch(() => {
        // Retry only on non-network related errors
        return sleep(retryDelay).then(() => {
            return fetchTokenFromHostAppForEnterpriseWithRetry(
                perfLog,
                retryCount - 1,
                retryDelay * 2,
                {
                    ...enterpriseParams,
                }
            );
        });
    });
}

export async function fetchTokenFromHostAppForEnterprise(
    perfLog: LogStartPerformanceUsage,
    enterpriseParams: EnterpriseTokenRequestParams
): Promise<TokenResponse> {
    const origin = getOrigin();

    if (enterpriseParams.resource == origin || enterpriseParams.resource == origin + '/') {
        const authTokenResponse = await getUserTokenIfAvailable(enterpriseParams);
        if (authTokenResponse?.AccessToken) {
            perfLog.addCheckpoint('UserTokenFetched');
            return authTokenResponse;
        }
    }

    perfLog.addCheckpoint('TokenRetrievalFromHostApp_Start');
    const token = await getResourceToken(
        enterpriseParams.resource,
        enterpriseParams.wwwAuthenticateHeader,
        enterpriseParams.mailboxInfo,
        enterpriseParams.scope,
        enterpriseParams.requestId,
        enterpriseParams.claims
    );

    if (token) {
        perfLog.addCheckpoint('TokenRetrievalFromHostApp_End');

        if (token?.AccessToken) {
            return token;
        }

        // donot fallback if we got back a SubError from OneAuth as this will cause failure in GATFR call as well
        if (token?.SubErrorCode == TokenResponseType.InteractionRequired) {
            return {};
        }
    }

    perfLog.addCheckpoint('FetchingSTSToken');

    const isMonarch = getApplicationSettings('PlatformType').isMonarch;
    if (isMonarch && isBootFeatureEnabled('auth-mon-removeFallbackToGATFR')) {
        perfLog.addCheckpoint('MonarchFallbackToGATFR');
        return {};
    }

    if (isBootFeatureEnabled('auth-hosted-removeFallbackToGATFR')) {
        perfLog.addCheckpoint('HostedFallbackToGATFR');
        return {};
    }

    return fetchTokenFromServer(perfLog, enterpriseParams);
}
